import { MetricValueMode, MultiTeamAggregate } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithQuery, atomWithSuspenseQuery } from 'jotai-tanstack-query';

import { atomWithDebounce } from '@/atoms/debounce';
import { filtersAtom } from '@/atoms/filters/filter';
import { competitionIdsToFilterByAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '@/atoms/filters/highLevel/seasons';
import { unwrappedTeamSelectedGamesForQueryAtom } from '@/atoms/filters/team/teamGames';
import { adjustedOffsetAtom, clientOrderParamsAtom, pageAndOrderParamsAtom } from '@/atoms/general';
import { metricValueModeAtom } from '@/atoms/table';
import { teamIdAtom } from '@/atoms/team/team';
import { createEnrichedDataAtom } from '@/atoms/utils/enrichment';
import { PREFETCH_ROW_COUNT } from '@/consts/pagination';
import { teamsNormalisedAggregatesUrl, teamsTotalAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString } from '@/utils/api';
import { limitArray, sortFetchResultObjectArray } from '@/utils/array';
import { addLink, flattenAndAddPrefix, optionsToValueStrings, toObject } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';

import { fetchClientAtom } from '../client';

export const teamAggsFilterParamsAtom = atom(get => ({
  lte: {
    ...toObject('input.game_date', get(filtersAtom).endDate),
  },
  gte: {
    ...toObject('input.game_date', get(filtersAtom).startDate),
  },
  in: {
    ...toObject('input.competition_id', get(competitionIdsToFilterByAtom)),
    ...toObject('input.season_id', get(seasonIdsToFilterByAtom)),
    ...toObject('output.team.team_id', get(filtersAtom).teamIds),
  },
}));

export const teamSquadFilterParamsAtom = atom(get => ({
  in: {
    ...toObject('input.team_id', get(teamIdAtom)),
    'input.game_id': optionsToValueStrings(get(unwrappedTeamSelectedGamesForQueryAtom)),
  },
}));

export const teamGameStatsFilterParamsAtom = atom(get => ({
  in: {
    'input.game_id': optionsToValueStrings(get(unwrappedTeamSelectedGamesForQueryAtom)),
  },
}));

export const teamAggsFilterParamsDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
const { debouncedValueAtom: debouncedTeamAggsFilterParamsAtom } = teamAggsFilterParamsDebounceObject;

const rawTeamAggsDataAtom = atomWithQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const adjustedOffset = get(adjustedOffsetAtom);
  const { order_by: orderBy } = get(pageAndOrderParamsAtom);
  const metricValueMode = get(metricValueModeAtom);
  const queryKey = [
    'teamAggs',
    adjustedOffset,
    orderBy,
    get(debouncedTeamAggsFilterParamsAtom),
    metricValueMode,
  ] as const;

  const queryFn = async ({
    queryKey: [, adjustedOffset, orderBy, teamAggsFilterParams, metricValueMode],
  }: {
    queryKey: typeof queryKey;
  }) => {
    const urlCreationFunction =
      metricValueMode === MetricValueMode.Per90 ? teamsNormalisedAggregatesUrl : teamsTotalAggregatesUrl;

    const data: MultiTeamAggregate[] = await fetch(
      urlCreationFunction({
        limit: PREFETCH_ROW_COUNT,
        offset: adjustedOffset,
        order_by: orderBy,
        ...teamAggsFilterParams,
      }),
    );
    return data.map(item => flattenAndAddPrefix(objSnakeToCamel(item), '.', false)) as NestedObject[];
  };
  const enabled = get(debouncedTeamAggsFilterParamsAtom) !== null;
  return { queryKey, queryFn, enabled };
});

const teamAggsDataWithLinksAtom = atom(get => {
  const { offset, limit } = get(pageAndOrderParamsAtom);
  const { data, isPending } = get(rawTeamAggsDataAtom);

  if (!data) return { data, isPending };

  const sortedPlayerAggsToDisplay = sortFetchResultObjectArray(data, get(clientOrderParamsAtom));
  const teamAggsData = limitArray(sortedPlayerAggsToDisplay, { offset: offset % PREFETCH_ROW_COUNT, limit });

  const dataWithLinks = teamAggsData.map(data =>
    addLink(data, data['team.name'] as string, data['team.teamId'] as number, 'teamLink'),
  );

  return {
    data: dataWithLinks,
    isPending,
  };
});

const enrichedTeamAggsDataAtom = createEnrichedDataAtom(
  atom(get => get(teamAggsDataWithLinksAtom).data),
  'team.teamId',
);

export const teamAggsDataAtom = atom(get => {
  const { isPending } = get(teamAggsDataWithLinksAtom);
  const enrichedData = get(enrichedTeamAggsDataAtom);

  return {
    data: enrichedData,
    isPending,
  };
});

const rawTeamAggsCountAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamAggsCount', get(debouncedTeamAggsFilterParamsAtom)] as const;
  const queryFn = async ({ queryKey: [, teamAggsFilterParams] }: { queryKey: typeof queryKey }) => {
    if (!teamAggsFilterParams) return null;

    return (await fetch(
      `/teams/normalised-aggregates/count${convertFilterParamsToString({
        ...teamAggsFilterParams,
      })}`,
    )) as Promise<number>;
  };
  return { queryKey, queryFn };
});

export const teamAggsCountAtom = atom(async get => (await get(rawTeamAggsCountAtom)).data);

export const unwrappedTeamAggsCountAtom = unwrap(teamAggsCountAtom);

export const hasRetrievedAllTeamAggsAtom = atom(get => {
  const teamAggsCount = get(unwrappedTeamAggsCountAtom);
  const teamAggsDataCount = get(rawTeamAggsDataAtom).data?.length;
  return (teamAggsCount && teamAggsDataCount && teamAggsCount === teamAggsDataCount) || false;
});
